import {useEffect, useState} from 'react';
import {useVirtualizer} from '@tanstack/react-virtual';
import {chatDayKey, chatDayLabel, groupChatMessages} from '../chatExperience';

export default function VirtualMessageList({className, messages, scrollRef, onScroll, header, loading, empty, renderMessage, previousDay, estimateSize = 104, target}) {
    const virtualizer = useVirtualizer({
        count: messages.length, getScrollElement: () => scrollRef.current, estimateSize: () => estimateSize,
        overscan: 10, getItemKey: index => messages[index]?.id ?? index, initialRect: {width: 800, height: 600},
    });
    const [highlighted, setHighlighted] = useState(null);
    const targetIndex = target ? messages.findIndex(message => message.id === target.id) : -1;
    useEffect(() => {
        if (!target || targetIndex < 0) { setHighlighted(null); return; }
        let frame;
        let attempts = 0;
        let cancelled = false;
        setHighlighted(target.id);
        virtualizer.scrollToIndex(targetIndex, {align: 'center'});
        const focusTarget = () => {
            if (cancelled) return;
            const container = scrollRef.current;
            const row = container?.querySelector(`[data-message-id="${target.id}"]`);
            if (!row && attempts++ < 20) { frame = window.requestAnimationFrame(focusTarget); return; }
            if (row && container) {
                const rowRect = row.getBoundingClientRect();
                const containerRect = container.getBoundingClientRect();
                container.scrollTop += rowRect.top - containerRect.top - (container.clientHeight - rowRect.height) / 2;
                row.focus({preventScroll: true});
            }
        };
        frame = window.requestAnimationFrame(focusTarget);
        const timer = window.setTimeout(() => setHighlighted(null), 2500);
        return () => { cancelled = true; window.cancelAnimationFrame(frame); window.clearTimeout(timer); };
    }, [target, targetIndex, virtualizer, scrollRef]);
    const virtualItems = virtualizer.getVirtualItems();
    const visibleItems = virtualItems.length > 0 ? virtualItems : messages.slice(0, Math.min(messages.length, 20)).map((_, index) => ({index, start: index * estimateSize}));
    return <div className={className} ref={scrollRef} onScroll={onScroll} tabIndex={0} role="region" aria-label={className === 'chat-thread-replies' ? 'Thread messages' : 'Conversation messages'}>
        {header}{loading}{empty}
        <div className="chat-message-virtual" style={{height: virtualizer.getTotalSize()}}>
            {visibleItems.map(row => {
                const message = messages[row.index];
                const previous = messages[row.index - 1];
                const previousDate = previous?.created_at || previousDay;
                const startsDay = !previousDate || chatDayKey(previousDate) !== chatDayKey(message.created_at);
                return <div key={message.id} ref={virtualizer.measureElement} data-index={row.index} data-message-id={message.id} tabIndex={-1} className={`chat-message-virtual-row${highlighted === message.id ? ' is-navigation-target' : ''}`} style={{top: row.start}}>
                    {startsDay ? <div className="chat-day-divider"><span>{chatDayLabel(message.created_at)}</span></div> : null}
                    {renderMessage(message, {grouped: groupChatMessages(message, previous)})}
                </div>;
            })}
        </div>
    </div>;
}
